/**
 * IndexedDB Service
 * Manages local storage for transcription history
 * Uses 'idb' library for simplified IndexedDB operations
 */

import { openDB } from 'idb';

const DB_NAME = 'voicetudu-db';
const DB_VERSION = 1;
const STORE_NAME = 'history';

const MAX_HISTORY_ITEMS = 50; // Maximum items to store

/**
 * Initialize the database
 * @returns {Promise<IDBPDatabase>}
 */
async function initDB() {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion, newVersion, transaction) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, {
          keyPath: 'id',
          autoIncrement: true
        });
        // Create indexes for searching
        store.createIndex('date', 'date', { unique: false });
        store.createIndex('fileName', 'fileName', { unique: false });
        store.createIndex('favorite', 'favorite', { unique: false });
      }
    }
  });
}

/**
 * Save a new history item
 * @param {Object} data - The transcription result data
 * @param {string} data.fileName - Original file name
 * @param {number} data.fileSize - File size in bytes
 * @param {string} data.fileType - MIME type
 * @param {Object} data.result - The full result object (transcription, summary, tasks, email)
 * @returns {Promise<Object>} Saved item with generated id
 */
export async function saveHistoryItem(data) {
  const db = await initDB();
  
  const item = {
    fileName: data.fileName,
    fileSize: data.fileSize,
    fileType: data.fileType,
    result: data.result,
    date: new Date().toISOString(),
    favorite: false,
    edited: false
  };

  const id = await db.add(STORE_NAME, item);
  
  // Clean up old items if exceeding limit
  await cleanupOldItems();
  
  await db.close();
  
  return { ...item, id };
}

/**
 * Get all history items, sorted by date (newest first)
 * @returns {Promise<Array>}
 */
export async function getAllHistory() {
  const db = await initDB();
  const items = await db.getAll(STORE_NAME);
  await db.close();
  
  // Sort by date descending
  return items.sort((a, b) => new Date(b.date) - new Date(a.date));
}

/**
 * Get a single history item by ID
 * @param {number} id
 * @returns {Promise<Object|null>}
 */
export async function getHistoryItem(id) {
  const db = await initDB();
  const item = await db.get(STORE_NAME, id);
  await db.close();
  return item || null;
}

/**
 * Update an existing history item
 * @param {number} id
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>}
 */
export async function updateHistoryItem(id, updates) {
  const db = await initDB();
  
  const existing = await db.get(STORE_NAME, id);
  if (!existing) {
    await db.close();
    throw new Error('History item not found');
  }
  
  const updated = {
    ...existing,
    ...updates,
    id // Ensure id is preserved
  };
  
  await db.put(STORE_NAME, updated);
  await db.close();
  
  return updated;
}

/**
 * Delete a history item
 * @param {number} id
 * @returns {Promise<boolean>}
 */
export async function deleteHistoryItem(id) {
  const db = await initDB();
  await db.delete(STORE_NAME, id);
  await db.close();
  return true;
}

/**
 * Toggle favorite status
 * @param {number} id
 * @returns {Promise<Object>}
 */
export async function toggleFavorite(id) {
  const db = await initDB();
  const item = await db.get(STORE_NAME, id);
  
  if (!item) {
    await db.close();
    throw new Error('History item not found');
  }
  
  item.favorite = !item.favorite;
  await db.put(STORE_NAME, item);
  await db.close();
  
  return item;
}

/**
 * Clear all history
 * @returns {Promise<boolean>}
 */
export async function clearAllHistory() {
  const db = await initDB();
  await db.clear(STORE_NAME);
  await db.close();
  return true;
}

/**
 * Search history items
 * @param {string} query - Search query
 * @returns {Promise<Array>}
 */
export async function searchHistory(query) {
  const items = await getAllHistory();
  const lowerQuery = query.toLowerCase();
  
  return items.filter(item => {
    const inFileName = item.fileName.toLowerCase().includes(lowerQuery);
    const inSummary = item.result?.summary?.toLowerCase().includes(lowerQuery);
    const inTranscription = item.result?.transcription?.toLowerCase().includes(lowerQuery);
    return inFileName || inSummary || inTranscription;
  });
}

/**
 * Get favorites only
 * @returns {Promise<Array>}
 */
export async function getFavorites() {
  const db = await initDB();
  const tx = db.transaction(STORE_NAME, 'readonly');
  const store = tx.objectStore(STORE_NAME);
  const index = store.index('favorite');
  const items = await index.getAll(true);
  await db.close();
  
  return items.sort((a, b) => new Date(b.date) - new Date(a.date));
}

/**
 * Cleanup old items to keep storage under limit
 * Removes oldest non-favorite items first
 */
async function cleanupOldItems() {
  const db = await initDB();
  const items = await db.getAll(STORE_NAME);
  
  if (items.length <= MAX_HISTORY_ITEMS) {
    await db.close();
    return;
  }
  
  // Sort by date ascending (oldest first)
  const sorted = items.sort((a, b) => new Date(a.date) - new Date(b.date));
  
  // Remove oldest non-favorite items
  const toDelete = [];
  let remaining = items.length;
  
  for (const item of sorted) {
    if (remaining <= MAX_HISTORY_ITEMS) break;
    if (!item.favorite) {
      toDelete.push(item.id);
      remaining--;
    }
  }
  
  // If still over limit, delete oldest favorites too
  for (const item of sorted) {
    if (remaining <= MAX_HISTORY_ITEMS) break;
    toDelete.push(item.id);
    remaining--;
  }
  
  for (const id of toDelete) {
    await db.delete(STORE_NAME, id);
  }
  
  await db.close();
}

/**
 * Get storage statistics
 * @returns {Promise<Object>}
 */
export async function getStorageStats() {
  const items = await getAllHistory();
  const totalSize = items.reduce((acc, item) => acc + (item.fileSize || 0), 0);
  
  return {
    totalItems: items.length,
    totalSize,
    favorites: items.filter(i => i.favorite).length,
    edited: items.filter(i => i.edited).length
  };
}

export default {
  saveHistoryItem,
  getAllHistory,
  getHistoryItem,
  updateHistoryItem,
  deleteHistoryItem,
  toggleFavorite,
  clearAllHistory,
  searchHistory,
  getFavorites,
  getStorageStats
};
